// ============================================================
// useDevisEditor — l'état d'édition d'un devis.
// ------------------------------------------------------------
// Le prix affiché en cours de saisie est calculé par
// `shared/pricing.js`, LE MÊME module que celui du serveur. Ce n'est
// pas une approximation d'affichage : c'est la même fonction, avec
// les mêmes entrées, donc le même résultat au centime.
//
// C'est tout l'intérêt de `shared/` — et c'est exactement ce qui
// manquait chez AGC, où la formule vivait en double (backend et
// data.jsx) et où les deux avaient déjà divergé.
//
// Le prix qui FAIT FOI reste celui que le serveur renvoie à
// l'enregistrement. L'affichage en direct sert à voir l'effet d'une
// saisie sans aller-retour réseau.
// ============================================================

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { corpsEnregistrement, fusionnerReponse, nouvelleCle } from './enregistrement.js';
import { declarer } from './champsTouches.js';
import { gammeProposee } from './gammeProposee.js';
import {
  computePiece,
  computeQuantityTiers,
  computeDevisTotal,
  encartMatiere,
} from '@shared/pricing.js';
import { brutDepuisGeometrie } from '@shared/brutDeLaPiece.js';

export function emptyPiece() {
  return {
    id: null,
    name: '',
    ref: '',
    qty: 1,
    density: null,
    price_per_kg: null,
    material_label: '',
    // Le barreau : l'enveloppe prismatique, toujours calculable et la
    // plus neutre — le même défaut que `deduireFormeBrut` (§3.4).
    //
    // C'était `'box'`, l'ancien vocabulaire, que le schéma refuse
    // depuis le lot B : **toute pièce ajoutée à la main rendait le devis
    // inenregistrable en 400.** Troisième occurrence de la même famille
    // en deux lots, après le champ « Marge » et les boutons de forme —
    // et à chaque fois le symptôme est le même : un écran resté sur un
    // vocabulaire que le modèle a quitté.
    raw_shape: 'barreau',
    raw_l: null,
    raw_w: null,
    raw_h: null,
    raw_diameter: null,
    raw_length: null,
    is_purchase: false,
    purchase_price: null,
    purchase_coef: null,
    min_invoice: null,
    rounding_rule: 'none', // l'arrondi se choisit (29 septembre 2026)
    notes_internal: '',
    notes_client: '',
    cycle_time_min: null,
    setup_time_min: null,
    programming_time_min: null,
    cycle_time_min_ia: null,
    setup_time_min_ia: null,
    programming_time_min_ia: null,
    prises: [],
    quantities: [],
    subcontracting: [],
  };
}

export function emptySubcontracting() {
  return {
    id: null,
    supplier_id: null,
    label: '',
    mode: 'flat',
    // `null` et non 0 : un prix pas encore saisi n'est pas un
    // traitement gratuit. `computePiece` le signale et la pièce reste
    // incomplète jusqu'à ce qu'un prix soit mis.
    unit_price: null,
    min_invoice: null,
    purchase_coef: null,
    lead_days: null,
  };
}

export function useDevisEditor(initialDevis, initialPieces, defaults) {
  const [devis, setDevis] = useState(initialDevis);
  const [pieces, setPieces] = useState(initialPieces);
  // Suivi `dirty` : la garde de navigation et l'enregistrement
  // automatique avant génération de PDF en dépendent.
  const [dirty, setDirty] = useState(false);
  /**
   * La provenance de chaque champ, pièce par pièce — `{ ref: 'ai' }`.
   *
   * Tenue À CÔTÉ des pièces et non dedans : `pieceSchema` est `.strict()`
   * et refuserait une clé inconnue à l'enregistrement. C'est une
   * information d'affichage, pas une donnée métier ; elle vit le temps
   * de la saisie et disparaît à l'enregistrement, quand toutes les
   * valeurs sont devenues celles du deviseur.
   */
  const [sources, setSources] = useState(() => initialPieces.map(() => ({})));
  /**
   * Une clé par pièce, À CÔTÉ des pièces comme `sources` et pour la même
   * raison : `pieceSchema` est strict. Elle relie une pièce de l'écran à
   * la pièce envoyée, quand la réponse arrive après une nouvelle frappe
   * (`enregistrement.js`).
   */
  const [cles, setCles] = useState(() => initialPieces.map(() => nouvelleCle()));
  /** Monte à CHAQUE modification. C'est lui qui dit « on a tapé pendant l'envoi ». */
  const [revision, setRevision] = useState(0);
  const modifie = useCallback(() => {
    setDirty(true);
    setRevision((r) => r + 1);
  }, []);

  // Le dernier état RENDU, pour l'enregistrement asynchrone : une
  // requête qui revient doit comparer à l'écran d'aujourd'hui, pas à
  // celui de son départ.
  const etat = useRef({ devis, pieces, cles, revision, dirty });
  useLayoutEffect(() => {
    etat.current = { devis, pieces, cles, revision, dirty };
  });

  const patchDevis = useCallback((patch) => {
    setDevis((d) => ({ ...d, ...patch }));
    modifie();
  }, [modifie]);

  /**
   * Modifie une pièce — ET DÉCLARE LE GESTE.
   *
   * C'est le point de passage UNIQUE des modifications humaines : ses
   * trois appelants sont un `onChange` de champ ou un clic de bouton.
   * Ce qui écrit une pièce sans qu'un humain ait rien touché —
   * `applyEstimation`, `addPieceFromProposal` — passe par `setPieces` et
   * ne déclare rien. C'est cette asymétrie qui fait que
   * `ai_corrections` ne se remplira que de vraies corrections.
   *
   * Voir `champsTouches.js` : le geste se déclare, il ne se déduit
   * jamais d'une comparaison de valeurs (§11, ligne 11).
   */
  const patchPiece = useCallback((index, patch) => {
    setPieces((list) => list.map((p, i) => {
      if (i !== index) return p;
      const next = { ...p, ...patch, champs_touches: declarer(p, patch) };
      // Une cote de la PIÈCE change : le brut se recalcule ici, tout de
      // suite, par la fonction du serveur — sans attendre l'enregistrement
      // (2026-10-027, 22/10/2026 : la cote saisie ne changeait rien à
      // l'écran). Le serveur refait le même calcul en enregistrant.
      return 'geometry' in patch ? brutDepuisGeometrie(next, defaults) : next;
    }));
    modifie();
  }, [modifie, defaults]);

  /**
   * Applique une estimation de l'agent 2 à une pièce.
   *
   * **Elle ne touche PAS les temps retenus** : elle remplit la colonne
   * « valeur IA » et les prises. C'est le deviseur qui valide, et c'est
   * l'écart entre les deux colonnes qui mesure l'apprentissage.
   */
  const applyEstimation = useCallback((index, estimation) => {
    setPieces((list) =>
      list.map((p, i) =>
        i === index
          ? {
              ...p,
              cycle_time_min_ia: estimation.temps_cycle_min_par_piece,
              setup_time_min_ia: estimation.temps_reglage_min_par_lot,
              programming_time_min_ia: estimation.temps_programmation_min_par_projet,
              // La justification voyage avec le nombre. Sans elle,
              // l'écran ne montre qu'un chiffre à prendre ou à laisser.
              justification_cycle: estimation.justification_cycle ?? null,
              justification_reglage: estimation.justification_reglage ?? null,
              justification_programmation: estimation.justification_programmation ?? null,
              // ── LA GAMME, et plus les prises ──
              //
              // Les prises étaient une justification affichée à côté des
              // trois temps ; la gamme EST ce dont les temps dérivent.
              // Elle remplace donc la gamme courante — c'est une
              // nouvelle proposition, pas un ajout.
              gamme: gammeProposee(estimation.gamme, p.gamme, 'ia'),
              // Une nouvelle gamme rend les totaux figés caducs : c'est
              // le conflit du §8.4, et il se DEMANDE. On ne lève donc
              // pas le drapeau ici — `computePiece` signale l'écart et
              // l'écran pose la question.
              temps_avertissements: estimation.warnings ?? [],
            }
          : p,
      ),
    );
    modifie();
  }, [modifie]);

  const addPiece = useCallback(() => {
    setPieces((list) => [...list, emptyPiece()]);
    setSources((list) => [...list, {}]);
    setCles((list) => [...list, nouvelleCle()]);
    modifie();
  }, [modifie]);

  /**
   * Crée une pièce à partir d'une lecture de plan.
   *
   * Trois choses voyagent avec elle, et les trois comptent :
   *
   *  - `proposition_id`, que le serveur relira à l'enregistrement pour
   *    comparer ce qu'il avait proposé à ce qui a été retenu. C'est le
   *    chaînon de la couche 2 : sans lui, la capture écrirait du vide ;
   *  - la provenance de chaque champ, pour que la pastille dise « lu
   *    sur le plan » ou « repris d'un devis passé » et pas seulement
   *    « saisi » ;
   *  - les opérations SANS taux et les traitements SANS prix. Une
   *    entrée manquante ne vaut pas zéro : la pièce naît incomplète et
   *    ça se voit, plutôt que d'afficher un prix qui aurait l'air sûr.
   */
  const addPieceFromProposal = useCallback(
    ({ piece, sources: fieldSources = {}, treatments = [], propositionId }) => {
      const created = {
        ...emptyPiece(),
        ...piece,
        proposition_id: propositionId ?? null,
        // La géométrie calculée, les entités lues et les tolérances
        // critiques voyagent avec la pièce : sans elles, le bloc
        // pré-chiffrage n'a rien à afficher et l'agent 2 rien à lire.
        geometrie: piece.geometrie ?? null,
        entites: piece.entites ?? [],
        tolerances_critiques: piece.tolerances_critiques ?? [],
        // Un traitement INTERNE porte un temps, un traitement
        // SOUS-TRAITÉ un prix. Ni l'un ni l'autre n'est inventé : la
        // pièce reste visiblement incomplète tant qu'il manque.
        subcontracting: treatments.map((t) => ({
          id: null,
          treatment_id: t.treatment_id ?? null,
          nature: t.nature ?? 'sous_traite',
          temps_min: null,
          supplier_id: t.supplier_id ?? null,
          label: t.label,
          mode: t.mode ?? 'flat',
          unit_price: null,
          min_invoice: null,
          purchase_coef: null,
          lead_days: null,
        })),
      };
      setPieces((list) => [...list, created]);
      setSources((list) => [...list, fieldSources]);
      setCles((list) => [...list, nouvelleCle()]);
      modifie();
    },
    [modifie],
  );

  const removePiece = useCallback((index) => {
    setPieces((list) => list.filter((_, i) => i !== index));
    setSources((list) => list.filter((_, i) => i !== index));
    setCles((list) => list.filter((_, i) => i !== index));
    modifie();
  }, [modifie]);

  /** Duplique une pièce avec TOUS ses champs — l'oubli d'un seul
   *  produirait un prix différent sans que personne le voie. */
  const duplicatePiece = useCallback((index) => {
    setPieces((list) => {
      const source = list[index];
      const copy = {
        ...structuredClone(source),
        id: null,
        name: source.name ? `${source.name} (copie)` : '',
      };
      copy.subcontracting = copy.subcontracting.map((s) => ({ ...s, id: null }));
      return [...list.slice(0, index + 1), copy, ...list.slice(index + 1)];
    });
    // Une copie n'hérite PAS de la proposition d'origine : elle n'a pas
    // été lue sur un plan, et la compter comme telle fausserait la
    // mesure de l'apprentissage.
    setSources((list) => [...list.slice(0, index + 1), {}, ...list.slice(index + 1)]);
    setCles((list) => [...list.slice(0, index + 1), nouvelleCle(), ...list.slice(index + 1)]);
    modifie();
  }, [modifie]);

  /** Prix calculés en direct, par le module partagé. Les paliers
   *  passent par la même fonction que la pièce, en ne changeant que la
   *  quantité — donc pas une ligne de calcul de plus. */
  const priced = useMemo(
    () =>
      pieces.map((piece) => ({
        piece,
        result: computePiece(piece, { defaults }),
        tiers: computeQuantityTiers(
          piece,
          (piece.quantities ?? []).map((q) => q.qty),
          { defaults },
        ),
      })),
    [pieces, defaults],
  );

  const totals = useMemo(() => computeDevisTotal(pieces, { defaults }), [pieces, defaults]);

  /** L'encart matière et transport du devis — §1.1 et §1.3. */
  const encart = useMemo(
    () => encartMatiere(devis, priced.map((p) => p.result)),
    [devis, priced],
  );

  /** Corps d'enregistrement — voir `enregistrement.js`. */
  const payload = useMemo(() => corpsEnregistrement(devis, pieces), [devis, pieces]);

  /** Ce qui part : le corps, et de quoi reconnaître la réponse au retour. */
  const instantane = useCallback(() => {
    const e = etat.current;
    return { payload: corpsEnregistrement(e.devis, e.pieces), cles: e.cles, revision: e.revision, pieces: e.pieces };
  }, []);

  /**
   * La réponse du serveur, appliquée SANS effacer ce qu'on a tapé pendant
   * l'envoi. Rend `true` s'il ne reste rien à envoyer.
   */
  const appliquerReponse = useCallback((envoye, reponse) => {
    const r = fusionnerReponse({ local: etat.current, envoye, reponse });
    // Tout de suite, sans attendre le rendu : si l'envoi doit repartir,
    // il repart de CET état — une pièce nouvelle porte déjà son id, et
    // n'est pas créée une seconde fois.
    etat.current = { ...etat.current, devis: r.devis, pieces: r.pieces, cles: r.cles, dirty: !r.propre };
    setDevis(r.devis);
    setPieces(r.pieces);
    setCles(r.cles);
    if (r.propre) {
      setSources(r.pieces.map(() => ({})));
      setDirty(false);
    }
    return r.propre;
  }, []);

  const markSaved = useCallback((savedDevis, savedPieces) => {
    setDevis(savedDevis);
    setPieces(savedPieces);
    // La proposition a été consommée : la renvoyer au prochain
    // enregistrement recréerait les mêmes corrections à chaque clic.
    setSources(savedPieces.map(() => ({})));
    setCles(savedPieces.map(() => nouvelleCle()));
    setDirty(false);
  }, []);

  return {
    devis,
    pieces,
    priced,
    totals,
    encart,
    dirty,
    revision,
    etat,
    payload,
    instantane,
    appliquerReponse,
    sources,
    patchDevis,
    patchPiece,
    addPiece,
    addPieceFromProposal,
    applyEstimation,
    removePiece,
    duplicatePiece,
    markSaved,
  };
}
